import { FormattedMessage, useIntl } from "react-intl";
import styles from "./CoursewareDialog.module.scss";
import { clsx } from "clsx";
import {
  ReactNode,
  RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import CopyButtonWithFeedback from "../../../../common/core-ui/CopyButtonWithFeedback.tsx";
import EditCopy from "../../../../common/icons/EditCopy.tsx";
import MiscellaneousEyeOpen from "../../../../common/icons/MiscellaneousEyeOpen.tsx";
import { useRevealFetcher } from "./courseware.ts";
import DefaultError from "../../../../common/core-ui/DefaultError.tsx";
import { Dialog } from "../../../../common/core-ui/Dialog.tsx";
import AssetCover from "../../../../common/features/asset/AssetCover.tsx";
import MiscellaneousICircle from "../../../../common/icons/MiscellaneousICircle.tsx";
import MiscellaneousWarning from "../../../../common/icons/MiscellaneousWarning.tsx";
import {
  License,
  ManageAsset,
} from "../../../../common/api/decimal/common-types/manageAssets.ts";
import LoaderButton from "../../../../common/core-ui/LoaderButton.tsx";

function DefaultWarning() {
  return (
    <p className={clsx(styles.revealWarning, "vst-icon-with-text")}>
      <MiscellaneousICircle />
      <span>
        <FormattedMessage
          defaultMessage="Some items cannot be returned once the code is revealed. <a>Review Return Policy</a>"
          values={{
            a: (chunks: ReactNode) => (
              <BaseLinkOrButton
                className="vst-link vst-link-size-small"
                href={
                  "https://support.vitalsource.com/hc/en-us/articles/211374668-VitalSource-Return-Policy"
                }
              >
                {chunks}
              </BaseLinkOrButton>
            ),
          }}
        />
      </span>
    </p>
  );
}

function RevealCode({
  showError,
  submitting,
}: {
  showError: boolean;
  submitting: boolean;
}) {
  const errorId = useId();

  return (
    <>
      <DefaultWarning />
      <LoaderButton
        loading={submitting}
        className={clsx("vst-button-pill", styles.revealButton)}
        type="submit"
        aria-describedby={errorId}
      >
        <MiscellaneousEyeOpen />
        <FormattedMessage defaultMessage="Reveal Access Code" />
      </LoaderButton>
      {showError && !submitting && (
        <div className={styles.errorBox} id={errorId}>
          <DefaultError />
        </div>
      )}
    </>
  );
}

function CopyCode({
  accessCode,
  copyBtnRef,
}: {
  accessCode: string;
  copyBtnRef: RefObject<HTMLButtonElement>;
}) {
  const fieldId = useId();

  useEffect(() => {
    copyBtnRef.current?.focus();
  }, [copyBtnRef]);

  return (
    <>
      <DefaultWarning />
      <div className="vst-input-with-button">
        <label htmlFor={fieldId}>
          <FormattedMessage defaultMessage="Access Code" />
        </label>
        <input value={accessCode} readOnly id={fieldId} />
        <CopyButtonWithFeedback ref={copyBtnRef} textToCopy={accessCode}>
          <EditCopy />
          <FormattedMessage defaultMessage="Copy" />
        </CopyButtonWithFeedback>
      </div>
    </>
  );
}

function CodeAlreadyRevealed() {
  const fieldId = useId();
  const intl = useIntl();

  return (
    <>
      <div
        className={clsx(styles.alreadyRevealedWarning, "vst-icon-with-text")}
      >
        <MiscellaneousWarning />
        <div>
          <FormattedMessage
            defaultMessage="This code has already been revealed. If you need help accessing it again, <a>Contact Support</a>."
            values={{
              a: (chunks: ReactNode) => (
                <BaseLinkOrButton
                  className="vst-link vst-link-size-small"
                  href={"https://support.vitalsource.com/hc/en-us/requests/new"}
                >
                  {chunks}
                </BaseLinkOrButton>
              ),
            }}
          />
        </div>
      </div>
      <div className="vst-text-field">
        <label htmlFor={fieldId}>
          <FormattedMessage defaultMessage="Access Code" />
        </label>
        <input
          className={styles.alreadyRevealed}
          disabled
          value={intl.formatMessage({
            defaultMessage: "Already Revealed",
          })}
          readOnly
          id={fieldId}
        />
      </div>
    </>
  );
}

export default function CoursewareDialog({
  asset,
  license,
  onRequestClose,
}: {
  asset: ManageAsset;
  license: License;
  onRequestClose: () => void;
}) {
  const fetcher = useRevealFetcher();
  const submitting = fetcher.state !== "idle";
  const [redeemError, setRedeemError] = useState(false);
  const copyBtnRef = useRef<HTMLButtonElement>(null);
  const accessCode = fetcher.data?.ok ? fetcher.data.data : license.accessCode;

  useEffect(() => {
    if (fetcher.state === "loading" && fetcher.data) {
      if (!fetcher.data.ok) {
        setRedeemError(true);
      }
    }
  }, [fetcher.data, fetcher.state]);

  return (
    <Dialog canClose={!submitting} onRequestClose={onRequestClose}>
      <h2 className={styles.header}>
        <AssetCover vbid={asset.vbid} width={120} width2x={150} alt="" />
        <div className={styles.dataFields}>
          <div className={styles.title}>{asset.title}</div>
          <div className={styles.subtitle}>{asset.subtitle}</div>
        </div>
      </h2>
      <div className={styles.main}>
        <ol className={styles.instructionsList}>
          <li>
            <FormattedMessage
              defaultMessage="Confirm you have the correct item for your course <span>(VBID: <strong>{vbid}</strong>)</span>."
              values={{
                vbid: asset.vbid,
                strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
                span: (chunks: ReactNode) => (
                  <span className={styles.vbid}>{chunks}</span>
                ),
              }}
            />
          </li>
          <li>
            <FormattedMessage defaultMessage="Reveal and copy the access code." />
            {accessCode?.revealed ? (
              <div className={styles.revealBox}>
                {accessCode?.code ? (
                  <CopyCode
                    copyBtnRef={copyBtnRef}
                    accessCode={accessCode.code}
                  />
                ) : (
                  <CodeAlreadyRevealed />
                )}
              </div>
            ) : (
              <fetcher.Form
                method="post"
                action={`/actions/reveal/${asset.vbid}`}
                className={styles.revealBox}
                onSubmit={(event) => {
                  if (submitting) {
                    // we can't disable button due to a11y
                    event.preventDefault();
                  } else {
                    setRedeemError(false);
                  }
                }}
              >
                <RevealCode showError={redeemError} submitting={submitting} />
              </fetcher.Form>
            )}
          </li>
          {accessCode?.href ? (
            <li>
              <FormattedMessage defaultMessage="Visit the publisher or third party website to redeem your access code and start using your materials.*" />
              <BaseLinkOrButton
                href={accessCode?.href}
                className={clsx(
                  "vst-button-pill",
                  "vst-button-variant-outline",
                  styles.visitButton
                )}
              >
                <FormattedMessage defaultMessage="Go to Website" />
              </BaseLinkOrButton>
            </li>
          ) : (
            <li>
              <FormattedMessage
                defaultMessage="Code must be redeemed on a publisher’s or third party website. If you are unable to find the correct URL, please contact <a>Support</a>.*"
                values={{
                  a: (chunks) => (
                    <BaseLinkOrButton
                      className="vst-link"
                      href={
                        "https://support.vitalsource.com/hc/en-us/requests/new"
                      }
                    >
                      {chunks}
                    </BaseLinkOrButton>
                  ),
                }}
              />
            </li>
          )}
        </ol>
        <div className={styles.footer}>
          <FormattedMessage defaultMessage="* A Course Key may also be required, this may be found on a receipt or obtained from an instructor." />
        </div>
      </div>
    </Dialog>
  );
}
